/** 考试端接口的类型与调用:与 apps/api 的 /v1/exam 一一对应(A18 列表 / A10 检录 / A11 答题 / A12 成绩) */
import { withClientProof } from '../ui/exam-client.js';
import type { ApiClient } from './client.js';
import type { StudentQuestion } from './learning.js';
import type { UploadTicket } from './media.js';

/** book = 先预约场次(开放预约的考试,还没落座) */
export type EnterStep = 'book' | 'checkin' | 'wait' | 'answer' | 'result' | 'none';
export interface Enterability {
  can: boolean;
  step: EnterStep;
  reason: string;
  countdownSec: number | null;
  missing: string[];
}
export interface ProctorPolicy {
  checkin: boolean;
  faceCompare: boolean;
  captureIntervalSec: number;
  /** 低码率录屏(建议 #20:正式考默认开、按考场可关;正式考关闭要写理由) */
  screenRecord: boolean;
  /** 正式考关闭录屏的理由(后台才有) */
  screenRecordOffReason?: string;
  /** 按考场关闭录屏:考场 id → 理由(后台才有;改考场时设) */
  screenRecordRoomsOff?: Record<string, string>;
  aiDetect: boolean;
  tabSwitchThreshold: number;
  thirdView: 'off' | 'mark' | 'hold_score';
  mic: boolean;
  screenShare: boolean;
  leaveToleranceSec: number;
  /** PC 考试客户端(二期;后台才有):off 浏览器模式 / mark 必须用、异常只标记 / hold_score 必须用、异常成绩待审核 */
  pcClient?: 'off' | 'mark' | 'hold_score';
  pcClientConfig?: PcClientConfig;
}
/** PC 考试客户端参数(后台才有;没填的项按规则配置中心的默认) */
export interface PcClientConfig {
  procMode?: 'blacklist' | 'whitelist';
  blockGroups?: string[];
  blockExtra?: string[];
  allowExtra?: string[];
  procAction?: 'close' | 'mark';
  allowVm?: boolean;
  allowMultiDisplay?: boolean;
  /** 退出口令(4–12 位字母数字);不填由系统按考试生成 6 位数字 */
  exitCode?: string;
}
export interface ExamCard {
  recordId: string;
  number: string;
  registrationNumber: string;
  /** 这条记录的组别(模拟考卡片只按组别 + 语言列出) */
  group?: string;
  exam: {
    id: string;
    name: string;
    subject: string;
    /** 卡片上的「语言」(「Scratch 图形化」写「图形化」) */
    language?: string;
    nature: string;
    purpose: string;
    durationMin: number;
    lateLimitMin: number;
    thirdView: string;
    /** 所在考场要不要用 PC 考试客户端(二期):off 不要求 / mark、hold_score 必须用 */
    pcClient?: string;
    /** 监考员已放行改用浏览器 */
    pcClientPass?: boolean;
    checkin: boolean;
    makeupKind: string | null;
    appealDays: number;
  };
  room: {
    id: string;
    name: string;
    checkinAt: string | null;
    startAt: string | null;
    endAt: string | null;
  } | null;
  status: { checkin: string; answer: string; score: string; effectiveExam: string };
  enter: Enterability;
  score: {
    /** 小学低年级组(平台规则只给等级)= null */
    total: number | null;
    grade: string | null;
    passed: boolean | null;
    publishedAt: string | null;
    finalScore: number | null;
    finalRule: string | null;
  } | null;
  appealOpenUntil: string | null;
  /** 开放预约的考试:截止时间、本组别的场次、改约还剩几次、现在能不能约 / 改约 */
  booking?: {
    deadline: string | null;
    sessions: { key: string; startAt: string; endAt: string | null }[];
    bookedAt: string | null;
    rebookLeft: number;
    canChange: boolean;
  } | null;
}
/** 场次预约(A18-2):本组别的场次与剩余名额;mine = 我约的场次键 */
export interface BookingSessions {
  card: ExamCard;
  mine: string | null;
  deadline: string | null;
  rebookLeft: number;
  canChange: boolean;
  sessions: {
    key: string;
    checkinAt: string | null;
    startAt: string;
    endAt: string | null;
    capacity: number;
    remaining: number;
  }[];
}
export interface ExamList {
  serverTime: string;
  formal: ExamCard[];
  mock: ExamCard[];
}
/** 人工核验的结果:pending 等监考员 · released 已放行 · rejected 被拒(带理由,可重新拍照再申请) */
export interface ReleaseInfo {
  state: 'none' | 'pending' | 'released' | 'rejected';
  reason: string | null;
  at: string | null;
}
export interface CheckinInfo {
  /** 服务器时间:检录 / 等待页倒计时、迟到截止按它校准 */
  serverTime?: string;
  card: ExamCard;
  student: { name: string; faceTemplate: string; consentFace: boolean };
  proctor: ProctorPolicy;
  steps: ('camera' | 'photo' | 'screen')[];
  /** 本场按没有影像授权处理:检录不拍照,直接人工核验;不抓拍、不录屏、不开第三视角 */
  noMediaConsent?: boolean;
  release?: ReleaseInfo;
  /** 迟到截止:人工核验被拒后到这个时间前都能重新申请,过了按缺考 */
  lateUntil?: string | null;
}
/** 文件题校验通过的文件(A20-2):压缩包带 entries(里面有几个文件);message = 提示(如工程里没有积木) */
export interface CheckedFile {
  name: string;
  size: number;
  mediaKey: string;
  format: string;
  message: string | null;
  preview: { kind: string; entries?: number; entryNames?: string[]; manual?: boolean };
}
export interface CheckinResult {
  /**
   * checked_in / first_capture / manual_released / mismatch(比对没通过,可重拍)/
   * pending_manual(没有影像授权,或比对第 3 次没通过已自动提交人工核验)
   */
  status: string;
  similarity: number | null;
  canRetry?: boolean;
  /** 比对没通过时还能重拍几次(用完自动提交人工放行) */
  attemptsLeft?: number;
  /** 比对没通过的原因(没拍到清楚的人脸 / 和底片不像同一个人) */
  reason?: string;
  /** 进场提示(已采集底片 / 比对服务暂不可用改人工核验) */
  note?: string;
}
export interface ExamSessionQuestion {
  question: StudentQuestion;
  answer: unknown;
  version: number;
  status: string;
  submitNo: number;
  judge: { casesPassed: number; casesTotal: number; errorType: string | null } | null;
  /** ACM 题号(A、B、C…,按卷面顺序) */
  label?: string;
  /** Python / C++:最后一次「提交评测」的代码(没提交过为 null;交卷按它判) */
  submittedAnswer?: string | null;
}
/** ACM 参数:罚时 / 封榜 / 考中榜单对选手可见 */
export interface AcmConfig {
  penaltyMin: number;
  freezeMin: number;
  boardVisible: boolean;
}
export interface AcmCellView {
  solved: boolean;
  acMin: number | null;
  rejected: number;
  pending: number;
  frozen: number;
  firstBlood: boolean;
}
export interface AcmBoardRow {
  rank: number | null;
  name: string;
  me: boolean;
  solved: number;
  penalty: number;
  cells: Record<string, AcmCellView>;
}
export type AcmPhase = 'live' | 'frozen' | 'final';
export interface ExamNotice {
  id: string;
  kind: 'notice' | 'judge_delay' | string;
  text: string;
  extendMin: number;
  roomId: string | null;
  at: string;
  system: boolean;
}
export interface AcmSubmissionView {
  questionId: string;
  label: string;
  no: number;
  at: string;
  minute: number | null;
  verdict: 'accepted' | 'rejected' | 'pending' | 'ignored';
  errorType: string | null;
}
/** ACM 考中面板:自己的真实结果 + 榜单(封榜 / 揭榜)+ 考中公告 */
export interface AcmLive {
  serverTime: string;
  deadlineAt: string | null;
  config: AcmConfig;
  problems: { id: string; label: string }[];
  start: string | null;
  freezeAt: string | null;
  phase: AcmPhase;
  me: {
    solved: number;
    penalty: number;
    cells: Record<string, AcmCellView & { penalty: number }>;
    submissions: AcmSubmissionView[];
  };
  board: {
    phase: AcmPhase;
    total: number;
    computedAt: string;
    rows: AcmBoardRow[];
    mine: AcmBoardRow | null;
  } | null;
  boardNote: string | null;
  notices: ExamNotice[];
}
export interface ExamPulse {
  serverTime: string;
  answerStatus: string;
  /** 被监考员交卷 / 取消资格:答题页切到对应的结束页 */
  stopped?: { kind: 'forced' | 'disqualified'; reason: string | null } | null;
  deadlineAt: string | null;
  remainingSec: number | null;
  notices: ExamNotice[];
}
export const ACM_VERDICT_LABELS: Record<string, { text: string; tone: string }> = {
  accepted: { text: '通过', tone: 'ok' },
  rejected: { text: '未通过', tone: 'bad' },
  pending: { text: '评测中', tone: 'amber' },
  ignored: { text: '不计罚时', tone: '' },
};
export const ACM_PHASE_LABELS: Record<AcmPhase, string> = {
  live: '实时',
  frozen: '封榜中',
  final: '最终榜',
};
export interface ExamSession {
  sessionId: string | null;
  serverTime: string;
  record: {
    id: string;
    number: string;
    registrationNumber: string;
    answerStatus: string;
    deadlineAt: string | null;
    remainingSec: number | null;
    tabSwitches: number;
    startedAt: string | null;
    submittedAt: string | null;
  };
  exam: {
    id: string;
    name: string;
    nature: string;
    purpose: string;
    durationMin: number;
    proctor: ProctorPolicy;
    /** 所在考场要不要用 PC 考试客户端(二期):off / mark / hold_score */
    pcClient?: string;
    /** AI 画面识别:隔几秒抽一帧小图(0 / 没有 = 不抽;A20-3) */
    aiFrameSec?: number;
    contestFormat: string;
    judgeTiming: string;
    /** 考生看不看得到每题分值(考试设置;老数据没有这个字段按看得到) */
    showScores?: boolean;
    /** ACM 参数;非 ACM = null */
    acm?: AcmConfig | null;
  };
  room: { name: string; endAt: string | null } | null;
  questions: ExamSessionQuestion[];
}
export interface CodeStatus {
  status: string;
  submitNo?: number;
  casesPassed?: number | null;
  casesTotal?: number | null;
  errorType?: string | null;
  cases?: { status: string; timeMs: number }[] | null;
}
export interface SubmitResult {
  submittedAt: string;
  unanswered: string[];
  judging: number;
  answerStatus: string;
}
export interface ResultItem {
  no: number;
  /** ACM 题号 */
  label?: string;
  questionId: string;
  type: string;
  /** 小学低年级组为 null(逐题只给对错,A19-2) */
  maxScore: number | null;
  score: number | null;
  correct: boolean | null;
  errorType: string | null;
  casesPassed: number | null;
  feedback: string | null;
  explanation: string | null;
  stem: string | null;
  /** 这道题整题重判过(第几次、理由;自己的分数变了带上原来几分) */
  rejudged?: { seq: number; reason: string; before: number | null } | null;
}
export interface SampleRun {
  taskId: string | null;
  samples: { input: string; expected: string }[];
}
export interface SampleResult {
  status: string;
  errorType: string | null;
  compileError: string | null;
  cases:
    | {
        input: string;
        expected: string;
        status: string;
        timeMs: number;
        stdout: string;
        error: string | null;
      }[]
    | null;
}
export interface ExamResult {
  card: ExamCard;
  state: 'not_submitted' | 'disqualified' | 'pending' | 'pending_review' | 'appealing' | 'published';
  hideRank: boolean;
  /** 模拟考不排名:不出名次、组别排名、超越比例(只给分数和等级) */
  rankless?: boolean;
  /** 小学低年级组 got / total 为 null(只给掌握度) */
  knowledge: { id: string; name: string; got: number | null; total: number | null; mastery: number }[];
  /** 补考的两次成绩:首考 / 补考 / 最终(rule:max 取高分「含补考」· retake 以重考为准;A12-3) */
  makeupScores?: { first: number | null; retake: number | null; final: number | null; rule: string } | null;
  /** 成绩未发布时的预计发布时间(A19-1;定时发布 = 设定时间,其余是估计) */
  expectedPublishAt?: string | null;
  scores: {
    total: number | null;
    objective: number | null;
    code: number | null;
    subjective: number | null;
    grade: string | null;
    passed: boolean | null;
    rank: number | null;
    groupSize: number | null;
    surpass: number | null;
    finalScore: number | null;
    finalRule: string | null;
    publishedAt: string | null;
    /** ACM:通过题数与罚时(名次按它排) */
    acm?: { solved: number; penalty: number; penaltyMin: number } | null;
  } | null;
  contestFormat?: string;
  items: ResultItem[] | null;
  review: { verdict: string; tabSwitches: number; anomalies: number };
  appeal: {
    id: string;
    number: string;
    status: string;
    kind: string;
    text: string;
    verdictNote: string | null;
    createdAt: string;
    handledAt: string | null;
  } | null;
  appealOpen: boolean;
  /** 被取消资格:原因、时间、监考申诉截止(取消资格的不要求成绩发布就能提监考申诉) */
  disqualified?: { reason: string; at: string | null; appealUntil: string | null } | null;
  /** 认证证书(认证考):有效证书编号;申诉处理中的暂缓生成(没发的不发,已发的照常有效) */
  certificate?: { number: string | null; onHold: boolean } | null;
  makeup: { allowed: boolean; maxTimes: number; request: MakeupRequestState | null };
  explanationOpen: boolean;
}
/** 学生的补考申请走到哪了(考试端成绩页 / 家长端共用);note = 机构不批准的原因 */
export interface MakeupRequestState {
  status: 'pending' | 'approved' | 'rejected';
  note: string | null;
  at: string;
  decidedAt: string | null;
}
export const MAKEUP_REQUEST_LABELS: Record<string, { text: string; tone: string; hint: string }> = {
  pending: { text: '审核中', tone: 'amber', hint: '机构审核后通知,家长端同步' },
  approved: { text: '已批准', tone: 'ok', hint: '补考安排好后会通知时间与考场' },
  rejected: { text: '未批准', tone: 'bad', hint: '' },
};
export interface ProctorReport {
  sessionId: string;
  kind:
    | 'tab_switch'
    | 'capture'
    | 'capture_failed'
    /** 摄像头断开 / 打不开:抓拍中断(A11-3,只标记不拦交卷) */
    | 'capture_interrupted'
    | 'screen_share_stopped'
    | 'third_view_connected'
    | 'third_view_disconnected'
    | 'leave_seat'
    | 'paste_blocked'
    | 'screen_record_failed';
  mediaKey?: string;
  payload?: Record<string, unknown>;
}

export const QUESTION_TYPE_LABELS: Record<string, string> = {
  single: '单选题',
  multiple: '多选题',
  judge: '判断题',
  blank: '填空题',
  poem: '选字组诗',
  essay: '解答题',
  code_python: '编程 · Python',
  code_cpp: '编程 · C++',
  code_scratch: '编程 · Scratch',
  attachment: '附件上传',
  jr_blocks: '编程 · 图标积木',
};
/** 组别(考生端用;和后台 GROUP_LABELS 同一份叫法) */
export const EXAM_GROUP_LABELS: Record<string, string> = {
  primary_low: '小学低年级组',
  primary_high: '小学高年级组',
  junior: '初中组',
  senior: '高中组',
};
export const NATURE_LABELS: Record<string, string> = {
  formal: '正式',
  mock: '模拟',
  practice: '练习',
};
export const PURPOSE_LABELS: Record<string, string> = {
  certification: '认证考',
  contest: '赛事',
  assessment: '阶段测评',
  class_test: '班级测',
};
export const THIRD_VIEW_LABELS: Record<string, string> = {
  off: '第三视角 · 本场关闭',
  mark: '第三视角 · 本场开启 · 未完成只标记',
  hold_score: '第三视角 · 本场开启 · 未完成暂不出分',
};
export const ENTER_REASON_TEXT: Record<string, string> = {
  ok: '',
  not_published: '考试未发布',
  room_missing: '未分配考场,请联系考务',
  need_booking: '先预约场次,预约后才有检录入口',
  booking_closed: '预约已截止,没有预约场次;请联系考务安排',
  config_missing: '考场配置不完整,请联系考务',
  checkin_not_open: '检录时间未到',
  need_checkin: '检录已开放',
  not_started: '检录完成,等待开考',
  late: '已超过迟到上限,无法进入',
  ended: '考试已结束',
  submitted: '已交卷',
  disqualified: '已取消资格',
};
export const ERROR_TYPE_LABELS: Record<string, string> = {
  compile_error: '编译错误',
  wrong_answer: '答案错误',
  time_limit: '超时',
  memory_limit: '超内存',
  runtime_error: '运行错误',
  accepted: '通过',
};
export const CASE_STATUS_LABELS: Record<string, string> = {
  accepted: '通过',
  wrong_answer: '答案错误',
  time_limit: '超时',
  memory_limit: '超内存',
  runtime_error: '运行错误',
};
export const APPEAL_STATUS_LABELS: Record<string, string> = {
  submitted: '已提交',
  accepted: '已受理',
  reviewing: '复核中',
  upheld: '申诉成立',
  rejected: '申诉不成立',
};
export const REVIEW_VERDICT_LABELS: Record<string, string> = {
  none: '正常',
  normal: '正常',
  pending: '待复核',
  violation: '违纪',
};

export function examApi(api: ApiClient) {
  const b = '/v1/exam';
  const r = (id: string) => `${b}/records/${id}`;
  return {
    list: () => api.get<ExamList>(`${b}/list`),
    sessions: (id: string) => api.get<BookingSessions>(`${r(id)}/sessions`),
    book: (id: string, key: string) => api.post<BookingSessions>(`${r(id)}/book`, { key }),
    checkinInfo: (id: string) => api.get<CheckinInfo>(`${r(id)}/checkin`),
    // 检录 / 进入答题:在 PC 考试客户端里自动带上客户端凭证(浏览器里不带;要求客户端的考场会回 E0109)
    checkin: (id: string, body: { photoKey?: string; screenShared?: boolean; client?: string }) =>
      withClientProof(api, id, 'checkin', (headers) =>
        api.post<CheckinResult>(`${r(id)}/checkin`, body, { headers }),
      ),
    manualRelease: (id: string) => api.post<{ queued: boolean }>(`${r(id)}/manual-release`),
    /** 文件题传完一个文件就校验(A20-2):不合格回 V0003 / V0004 带原因;压缩包带回里面有几个文件 */
    checkFile: (id: string, body: { sessionId: string; questionId: string; key: string; name: string }) =>
      api.post<CheckedFile>(`${r(id)}/files/check`, body),
    /** 检录 / 等待阶段上报(还没有答题会话):屏幕共享被停止(A10-5) */
    checkinReport: (id: string, body: { kind: 'screen_share_stopped'; stage?: 'screen' | 'waiting' }) =>
      api.post<{ ok: boolean; skipped?: string }>(`${r(id)}/checkin/report`, body),
    enter: (id: string) =>
      withClientProof(api, id, 'enter', (headers) =>
        api.post<ExamSession>(`${r(id)}/enter`, undefined, { headers }),
      ),
    session: (id: string, sessionId: string) =>
      api.get<ExamSession>(`${r(id)}/session`, { query: { sessionId } }),
    save: (
      id: string,
      sessionId: string,
      answers: { questionId: string; answer: unknown }[],
      keepalive = false,
    ) =>
      api.put<{ saved: number; versions: Record<string, number>; serverTime: string }>(
        `${r(id)}/answers`,
        { sessionId, answers },
        keepalive ? { keepalive: true } : undefined,
      ),
    heartbeat: (id: string, sessionId: string) =>
      api.post<{ serverTime: string; deadlineAt: string | null; remainingSec: number | null }>(
        `${r(id)}/heartbeat`,
        { sessionId },
      ),
    submitCode: (id: string, sessionId: string, questionId: string, answer: string) =>
      api.post<{ submitNo: number; queued: boolean; version: number; feedback: string | null }>(
        `${r(id)}/code`,
        {
          sessionId,
          questionId,
          answer,
        },
      ),
    codeStatus: (id: string, questionId: string) => api.get<CodeStatus>(`${r(id)}/code/${questionId}`),
    runSample: (id: string, sessionId: string, questionId: string, answer: string) =>
      api.post<SampleRun>(`${r(id)}/code/run`, { sessionId, questionId, answer }),
    sampleStatus: (id: string, taskId: string) => api.get<SampleResult>(`${r(id)}/code/run/${taskId}`),
    report: (id: string, body: ProctorReport) => api.post<Record<string, unknown>>(`${r(id)}/proctor`, body),
    /** 检录照 / 抓拍的直传票(影像由浏览器直接传到存储;抓拍要带答题会话) */
    uploadTicket: (
      id: string,
      body: { purpose: 'checkin_photo' | 'capture'; contentType: string; size: number; sessionId?: string },
    ) => api.post<UploadTicket>(`${r(id)}/upload-ticket`, body),
    /** 录屏片段的直传票 */
    screenClipTicket: (
      id: string,
      body: { sessionId: string; contentType: string; size: number; from: string; to: string },
    ) => api.post<UploadTicket>(`${r(id)}/screen-clip/ticket`, body),
    /** 低码率录屏:上传一段(key = 直传后的媒体 key;base64 = 接口中转兜底,≤ 5 MB);from / to 用服务器时间 */
    screenClip: (
      id: string,
      body: {
        sessionId: string;
        contentType: string;
        base64?: string;
        key?: string;
        from: string;
        to: string;
        seq: number;
      },
    ) => api.post<{ ok: boolean; sec: number }>(`${r(id)}/screen-clip`, body),
    /** AI 画面识别抽帧(A20-3):flagged = 这一帧识别出的异常(只标记给监考员,答题页不弹窗) */
    aiFrame: (id: string, body: { sessionId: string; base64: string }) =>
      api.post<{ ok: boolean; flagged: string | null; skipped?: string }>(`${r(id)}/ai-frame`, body),
    /** auto = 时间到页面自动交的(截止到了服务端记「系统代交」,A11-8) */
    submit: (id: string, sessionId: string, auto = false) =>
      api.post<SubmitResult>(`${r(id)}/submit`, { sessionId, ...(auto ? { auto: true } : {}) }),
    result: (id: string) => api.get<ExamResult>(`${r(id)}/result`),
    /** ACM 面板(每 30 秒);since = 上次拉到的时间,只要那之后的公告 */
    acm: (id: string, since?: string) =>
      api.get<AcmLive>(`${r(id)}/acm`, since ? { query: { since } } : undefined),
    /** 每分钟一次:截止时间(延长马上同步)与考中公告 */
    pulse: (id: string, since?: string) =>
      api.get<ExamPulse>(`${r(id)}/pulse`, since ? { query: { since } } : undefined),
    /** 会话还在不在(答题页每 8 秒问一次,A11-6) */
    alive: (id: string, sid: string) =>
      api.get<{ state: 'ok' | 'taken_over' | 'stopped' | 'ended' }>(`${r(id)}/alive`, { query: { sid } }),
    appeal: (id: string, body: { kind: string; questionId?: string; text: string; attachments?: string[] }) =>
      api.post<{ id: string; number: string }>(`${r(id)}/appeal`, body),
    makeupApply: (id: string, note?: string) => api.post<{ ok: boolean }>(`${r(id)}/makeup-apply`, { note }),
  };
}
export type ExamApi = ReturnType<typeof examApi>;
